import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getNotes, addNote, updateNote, deleteNote } from '../api';

function formatDate(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

function NoteItem({ note, currentUserId, onEdit, onDelete }) {
  const isOwn = note.user_id === currentUserId;
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="py-2.5 border-b border-gray-100 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-gray-800 flex-1 whitespace-pre-wrap">{note.note_text}</p>
        {isOwn && (
          <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
            {confirmDelete ? (
              <>
                <button onClick={() => setConfirmDelete(false)}
                  className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                <button onClick={() => onDelete(note.id)}
                  className="text-xs text-red-600 hover:text-red-800 font-medium">Delete</button>
              </>
            ) : (
              <>
                <button onClick={() => onEdit(note)}
                  className="text-xs text-gray-400 hover:text-blue-600">Edit</button>
                <button onClick={() => setConfirmDelete(true)}
                  className="text-xs text-gray-400 hover:text-red-600">Delete</button>
              </>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-1">
        {note.added_by_name} — {formatDate(note.created_at)}
        {note.updated_at !== note.created_at && ' (edited)'}
      </p>
    </div>
  );
}

function NoteForm({ initialText = '', initialType = 'general', onSave, onCancel, saving }) {
  const [text, setText] = useState(initialText);
  const [type, setType] = useState(initialType);

  return (
    <div className="mt-3 space-y-2">
      <div className="flex gap-2">
        {['general', 'insurance'].map((t) => (
          <button key={t} onClick={() => setType(t)}
            className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
              type === t
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
            }`}>
            {t === 'general' ? 'General' : 'Insurance info'}
          </button>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder={type === 'insurance'
          ? 'e.g. Takes Apple Health but only Molina plan as of Jan 2026…'
          : 'Add a note about this facility…'}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      <div className="flex items-center gap-2">
        <button
          onClick={() => onSave(text.trim(), type)}
          disabled={!text.trim() || saving}
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
            text.trim() && !saving
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}>
          {saving ? 'Saving…' : 'Save note'}
        </button>
        <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-700 underline">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function FacilityNotes({ facilityId }) {
  const { isAuthenticated, user } = useAuth();
  const [open, setOpen]         = useState(false);
  const [notes, setNotes]       = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote]   = useState(null);
  const [saving, setSaving]     = useState(false);

  if (!isAuthenticated) return null;

  const fetchNotes = () => {
    setLoadingNotes(true);
    getNotes(facilityId)
      .then(setNotes)
      .catch(() => setNotes([]))
      .finally(() => setLoadingNotes(false));
  };

  const handleOpen = () => {
    if (!open) fetchNotes();
    setOpen((v) => !v);
    setShowForm(false);
    setEditingNote(null);
  };

  const handleSave = async (text, type) => {
    setSaving(true);
    try {
      if (editingNote) {
        await updateNote(editingNote.id, { note_text: text, note_type: type });
      } else {
        await addNote(facilityId, { note_text: text, note_type: type });
      }
      fetchNotes();
      setShowForm(false);
      setEditingNote(null);
    } catch { /* ignore, user can retry */ }
    finally { setSaving(false); }
  };

  const handleDelete = async (noteId) => {
    try {
      await deleteNote(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch { /* ignore */ }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setShowForm(true);
  };

  const generalNotes   = notes.filter((n) => n.note_type === 'general');
  const insuranceNotes = notes.filter((n) => n.note_type === 'insurance');
  const totalCount     = notes.length;

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <button
        onClick={handleOpen}
        className="text-sm text-gray-500 hover:text-blue-600 font-medium transition-colors flex items-center gap-1"
      >
        <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {open ? 'Hide notes' : `Notes${totalCount > 0 ? ` (${totalCount})` : ''}`}
      </button>

      {open && (
        <div className="mt-3">
          {loadingNotes ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : (
            <>
              {/* General notes */}
              {generalNotes.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">General</p>
                  {generalNotes.map((n) => (
                    <NoteItem key={n.id} note={n} currentUserId={user?.id}
                      onEdit={handleEdit} onDelete={handleDelete} />
                  ))}
                </div>
              )}

              {/* Insurance notes */}
              {insuranceNotes.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Insurance Info</p>
                  {insuranceNotes.map((n) => (
                    <NoteItem key={n.id} note={n} currentUserId={user?.id}
                      onEdit={handleEdit} onDelete={handleDelete} />
                  ))}
                </div>
              )}

              {notes.length === 0 && !showForm && (
                <p className="text-xs text-gray-400 mb-2">No notes yet.</p>
              )}

              {/* Add / edit form */}
              {showForm ? (
                <NoteForm
                  initialText={editingNote?.note_text || ''}
                  initialType={editingNote?.note_type || 'general'}
                  onSave={handleSave}
                  onCancel={() => { setShowForm(false); setEditingNote(null); }}
                  saving={saving}
                />
              ) : (
                <button onClick={() => setShowForm(true)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-1">
                  + Add note
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
